const LONG_FRAME_TYPES = ['long-animation-frame', 'longtask'];
const SETTLE_CAP_MS = 60_000;
const INPUT_EVENTS = ['pointerdown', 'keydown'];
const INPUT_LISTENER_OPTIONS = { capture: true, passive: true } as const;

export type SettleResult = { outcome: 'capped' } | { outcome: 'settled'; perf: number };

const afterPaint = (fn: () => void): void => {
  const run = () => setTimeout(fn, 0);
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
  else run();
};

const longFrameType = (): string | undefined => {
  if (typeof PerformanceObserver === 'undefined') return undefined;
  const supported = PerformanceObserver.supportedEntryTypes ?? [];
  return LONG_FRAME_TYPES.find(type => supported.includes(type));
};

/**
 * Settles at the end of the chain of long frames that follows `fromPerf`, once `quietMs` pass with no new
 * long frame. A buyer's first click or key press stops the chain growing: the frame it lands in counts,
 * later frames do not. Reports `capped` when the chain is still growing 60 seconds after `fromPerf`.
 *
 * @returns A cancel function, or undefined when the browser reports neither long frames nor long tasks
 */
export const watchSettle = (
  fromPerf: number,
  quietMs: number,
  onDone: (result: SettleResult) => void
): (() => void) | undefined => {
  const type = longFrameType();
  if (!type) return undefined;

  let latestEnd = fromPerf;
  let inputPerf: number | undefined;
  let quietFound = false;
  let done = false;
  let quietTimer: ReturnType<typeof setTimeout> | undefined;
  let capTimer: ReturnType<typeof setTimeout> | undefined;

  const take = (entries: PerformanceEntryList): void => {
    [...entries].sort((a, b) => a.startTime - b.startTime).forEach(entry => {
      if (quietFound) return;
      if (entry.startTime >= latestEnd + quietMs) {
        quietFound = true;
        return;
      }
      const end = entry.startTime + entry.duration;
      if (end <= fromPerf || (inputPerf !== undefined && entry.startTime > inputPerf)) return;
      latestEnd = Math.max(latestEnd, end);
    });
  };

  const onInput = (): void => {
    inputPerf ??= performance.now();
  };

  const observer = new PerformanceObserver(list => take(list.getEntries()));

  const stop = (): void => {
    done = true;
    observer.disconnect();
    if (quietTimer) clearTimeout(quietTimer);
    if (capTimer) clearTimeout(capTimer);
    INPUT_EVENTS.forEach(name => window.removeEventListener(name, onInput, INPUT_LISTENER_OPTIONS));
  };

  const finish = (result: SettleResult): void => {
    if (done) return;
    stop();
    onDone(result);
  };

  const check = (): void => {
    if (done) return;
    take(observer.takeRecords());
    const wait = latestEnd + quietMs - performance.now();
    if (quietFound || wait <= 0) finish({ outcome: 'settled', perf: latestEnd });
    else quietTimer = setTimeout(() => afterPaint(check), wait);
  };

  capTimer = setTimeout(() => finish({ outcome: 'capped' }), fromPerf + SETTLE_CAP_MS - performance.now());
  INPUT_EVENTS.forEach(name => window.addEventListener(name, onInput, INPUT_LISTENER_OPTIONS));
  observer.observe({ buffered: true, type });
  quietTimer = setTimeout(() => afterPaint(check), Math.max(0, fromPerf + quietMs - performance.now()));

  return stop;
};
